import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SearchX, Search } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { useStore } from "../context/StoreContext";
import { fetchProducts } from "../api";

const NAVY = "#1B1F5C";

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const { wishlist, cart, toggleWishlist, addToCart } = useStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProducts({ search: q })
      .then((data) => active && setProducts(data))
      .catch(() => active && setProducts([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [q]);

  return (
    <div className="bg-white min-h-screen">
      <Header cartCount={cart.length} wishlistCount={wishlist.length} />
      <main className="max-w-7xl mx-auto px-4 min-h-[55vh]">
        {/* Breadcrumb */}
        <div className="pt-4 text-left">
          <p className="text-[12px] font-bold" style={{ color: NAVY }}>
            <Link to="/" className="hover:text-[#35C3C1] transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">&gt;</span>
            <span>Search Results</span>
          </p>
        </div>

        <div className="mt-4 text-left">
          <h1 className="font-display font-extrabold text-2xl md:text-3xl" style={{ color: NAVY }}>
            Search results for “{q}”
          </h1>
          {!loading && (
            <p className="text-[13px] font-semibold text-gray-500 mt-1">
              {products.length} {products.length === 1 ? "toy" : "toys"} found
            </p>
          )}
        </div>

        {loading ? (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-gray-100 h-72 animate-pulse" />
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-10">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} wishlist={wishlist} toggleWishlist={toggleWishlist} addToCart={addToCart} />
            ))}
          </div>
        ) : (
          <div className="py-20 flex flex-col items-center text-center">
            <span className="h-20 w-20 rounded-full bg-[#F7F7FA] flex items-center justify-center">
              <SearchX size={34} className="text-gray-300" />
            </span>
            <p className="mt-4 font-display font-extrabold text-xl" style={{ color: NAVY }}>No toys found for “{q}”</p>
            <p className="mt-1 text-[13px] font-semibold text-gray-500 max-w-sm">
              Try a different keyword like “LEGO”, “teddy”, “science” or browse our collections.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/toys-games" className="px-6 h-11 rounded-full text-white text-[12px] font-extrabold tracking-widest inline-flex items-center" style={{ backgroundColor: NAVY }}>
                BROWSE TOYS & GAMES
              </Link>
              <Link to="/gifts" className="px-6 h-11 rounded-full border-2 border-[#35C3C1] text-[#1B1F5C] text-[12px] font-extrabold tracking-widest inline-flex items-center hover:bg-[#35C3C1] hover:text-white transition-colors">
                EXPLORE GIFTS
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SearchResults;
